import { useCallback, useRef, useState } from "react";
import {
  AgentRequestError,
  streamAgentResponse,
} from "services/aiChat/streamAgentResponse";

export const GREETING =
  "I'm an AI tool intended to help answer questions about your PCS move. How may I help?";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
}

/**
 * Maps an agent failure to user-facing copy. The hub-derived statuses are the
 * ones a healthy deployment can still produce per user/account, so they get
 * specific messages; everything else falls back to a generic retry prompt.
 */
export function errorMessageFor(error: unknown): string {
  if (error instanceof AgentRequestError) {
    switch (error.status) {
      case 401:
        return "Your session has expired. Please sign in again.";
      case 403:
        return "AI chat isn't available for your account.";
      case 503:
        return "AI chat is temporarily unavailable. Please try again later.";
      case 429:
        return "You've reached the usage limit for now. Please try again later.";
    }
  }
  return "Something went wrong. Please try again.";
}

/**
 * Owns a single chat session: the message list (seeded with the assistant
 * greeting), the in-progress input, and the streaming lifecycle against
 * pcs-ai-agents. The conversation id is generated once and reused for every
 * turn so the server can thread context (it does not echo the id back).
 */
export function useAiChat() {
  const conversationIdRef = useRef<string>();
  if (!conversationIdRef.current) {
    conversationIdRef.current = crypto.randomUUID();
  }
  const abortRef = useRef<AbortController | null>(null);

  const [messages, setMessages] = useState<ChatMessage[]>([
    { id: "greeting", role: "assistant", content: GREETING },
  ]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = useCallback(async () => {
    const trimmed = input.trim();
    if (trimmed === "" || isStreaming) {
      return;
    }

    setError(null);
    setInput("");

    const assistantId = crypto.randomUUID();
    setMessages((prev) => [
      ...prev,
      { id: crypto.randomUUID(), role: "user", content: trimmed },
      { id: assistantId, role: "assistant", content: "" },
    ]);
    setIsStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamAgentResponse({
        message: trimmed,
        conversationId: conversationIdRef.current as string,
        signal: controller.signal,
        onEvent: (event) => {
          if (event.type === "text-delta") {
            setMessages((prev) =>
              prev.map((message) =>
                message.id === assistantId
                  ? { ...message, content: message.content + event.delta }
                  : message
              )
            );
          }
        },
      });
    } catch (err) {
      if (controller.signal.aborted) {
        // Intentional cancel (the modal closed) — leave state as-is.
        return;
      }
      setError(errorMessageFor(err));
      // Drop the empty assistant placeholder when nothing streamed before the
      // failure, so a lone empty bubble doesn't linger above the error.
      setMessages((prev) =>
        prev.filter(
          (message) => !(message.id === assistantId && message.content === "")
        )
      );
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
      setIsStreaming(false);
    }
  }, [input, isStreaming]);

  /** Aborts any in-flight stream (called when the modal closes). */
  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  return { messages, input, setInput, isStreaming, error, send, cancel };
}
